import {
  AfterViewChecked,
  Component,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DialogTriggerDirective } from '../../directives/dialog-trigger.directive';
import { BuildVersion } from '../../models/api/core/build-version-info/build-version';
import { Action } from '../../models/core/action';
import { FormatPipe } from '../../pipes/format.pipe';
import { HelpLinkService } from '../../services/help-link/help-link.service';
import { LiveChatService } from '../../services/live-chat/live-chat.service';
import { MessageBus } from '../../services/message/message-bus.service';
import { BaseComponent } from '../base/base.component';
import { ButtonComponent } from '../core/buttons/button.component';
import { ToastComponent } from '../core/labels/toast.component';
import { ActionRowComponent } from '../core/panels/action-row.component';
import { DialogComponent } from '../core/panels/dialog.component';

@Component({
  selector: 'app-footer',
  imports: [
    DialogComponent,
    ActionRowComponent,
    ButtonComponent,
    FormatPipe,
    DialogTriggerDirective,
    ToastComponent,
  ],
  template: `
    <footer
      class="footer tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center sm:tw-pt-[100px]"
      [style.background-color]="
        transparent ? 'transparent' : 'var(--sed-grey-20-color)'
      "
      role="contentinfo"
    >
      <div
        class="tw-w-full tw-flex tw-justify-center tw-flex-col tw-items-center"
      >
        <div
          class="tw-max-w-[1300px] tw-gap-4 tw-pb-8"
          [class.tw-w-full]="!showFooterText"
        >
          <div
            class="tw-flex tw-flex-col tw-grow md:tw-flex-row tw-items-center"
            [class.tw-justify-between]="!showFooterText"
            [class.tw-justify-center]="showFooterText"
          >
            <div
              class="tw-flex tw-shrink-0 tw-items-center tw-pt-4 tw-pb-4 sm:tw-pl-4 sm:tw-justify-left"
            >
              @if (showFooterLogo) {
                <img
                  src="assets/images/omni-logo.png"
                  class="logo sm:tw-pr-4"
                  alt="Omni Logo"
                />
              }
            </div>
            <div
              class="footer-links-host tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-justify-center tw-pr-2 md:tw-justify-end"
            >
              <ul
                class="tw-m-0 tw-flex tw-list-none tw-flex-col tw-flex-wrap tw-gap-y-2 tw-p-0 tw-text-center md:tw-max-w-full md:tw-flex-row md:tw-justify-end md:tw-gap-x-1 md:tw-gap-y-2 lg:tw-gap-x-2"
              >
                @for (item of footer; track item) {
                  @if (item.key === 'about') {
                    <li class="tw-contents">
                      <button
                        type="button"
                        class="footer-btn tw-sentence tw-font-bold tw-px-3"
                        [innerHTML]="item.label"
                        appDialogTrigger
                        (click)="onAboutClick($event)"
                      ></button>
                    </li>
                  } @else if (item.key === 'support') {
                    <li class="tw-contents">
                      <button
                        type="button"
                        class="footer-btn tw-sentence tw-font-bold tw-px-3"
                        [innerHTML]="item.label"
                        appDialogTrigger
                        (click)="requestId = ''; onSupportClick()"
                      ></button>
                    </li>
                  } @else if (item.key === 'help') {
                    <li class="tw-contents">
                      <button
                        type="button"
                        class="footer-btn tw-sentence tw-font-bold tw-px-3"
                        [innerHTML]="item.label"
                        (click)="onHelpClick()"
                      ></button>
                    </li>
                  } @else if (item.key === 'whatsnew') {
                    @if (!whatsNew) {
                      <li class="tw-contents">
                        <a
                          class="footer-btn tw-sentence tw-font-bold tw-px-3"
                          [innerHTML]="item.label"
                          target="_blank"
                          [href]="item.routerLink"
                        ></a>
                      </li>
                    }
                  } @else {
                    <li class="tw-contents">
                      <a
                        class="footer-btn tw-sentence tw-font-bold tw-px-3"
                        [innerHTML]="item.label"
                        target="_blank"
                        [href]="item.routerLink"
                      ></a>
                    </li>
                  }
                }
              </ul>
            </div>
          </div>
        </div>
        @if (showFooterText) {
          <div
            class="tw-flex tw-justify-center tw-text-center tw-w-full tw-pb-4 tw-px-4"
          >
            <div class="tw-max-w-[1000px]">
              <p>
                Access to this information resource is restricted to authorized
                users only. This information resource may be monitored for
                administrative and security reasons. By proceeding, you consent
                to this monitoring. In order to protect the information
                entrusted to Sedgwick, unauthorized access and/or unauthorized
                use of this information resource may be prosecuted to the
                fullest extent of the law.
              </p>
            </div>
          </div>
        }
      </div>
      <div
        class="tw-rounded-t-md sm:tw-h-[56px] tw-h-full tw-w-full tw-flex tw-justify-center tw-flex-row tw-items-center"
        [style.background-color]="
          transparent ? 'transparent' : 'var(--sed-grey-40-color)'
        "
      >
        <div class="tw-pl-4 tw-pr-4 tw-pt-3 tw-w-full tw-max-w-[1300px]">
          <div
            class="tw-flex tw-flex-col tw-grow md:tw-flex-row md:tw-items-center"
            [class.tw-justify-between]="!showFooterText"
            [class.tw-justify-center]="showFooterText"
            [class.tw-gap-1]="showFooterText"
          >
            <div
              class="copyright tw-flex tw-justify-center md:tw-justify-start sm:tw-items-center tw-flex-row"
            >
              <i class="fa-light fa-copyright"></i>
              <span class="tw-pl-1">
                {{ buildVersion?.copyrightYear }} Sedgwick Claims Management
                Services, Inc. All Rights Reserved.
              </span>
            </div>
            <div
              class="tw-flex tw-justify-center md:tw-justify-end sm:tw-pt-0 tw-pt-2 sm:tw-pb-0 tw-pb-4"
            >
              <span class="tw-pr-1">
                Built {{ buildVersion?.compiledDate | format: 'dt' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>

    <app-dialog #aboutDialog label="About us" width="600px" appendTo="body">
      <ng-template #content>
        <div class="tw-py-4">
          <p>
            To access OmniInsights, you will need a recent version of Microsoft
            Edge, Google Chrome, Mozilla Firefox, or Safari. JavaScript must be
            enabled in the browser for the site to function correctly. To enable
            JavaScript, please consult your browser documentation.
          </p>
          <br />
          <p>
            Access to this website is restricted and is controlled based upon
            user identity and password. To obtain access to the information in
            this website, you need to be an authorized user. This website is
            monitored for unauthorized activity, and any unauthorized users will
            be prosecuted.
          </p>
          <br />
          <p>
            All rights reserved. No part of this information may be reproduced
            in any form or by any electronic or mechanical means including
            information storage and retrieval systems without the written
            permission of Sedgwick Claims Management Services, Inc.
          </p>
        </div>
        <app-action-row>
          <app-button
            right1
            (click)="aboutDialog.visible = false"
            label="Close"
          />
        </app-action-row>
      </ng-template>
    </app-dialog>

    <app-dialog
      #supportDialog
      label="Technical support"
      width="600px"
      appendTo="body"
    >
      <ng-template #content>
        <div class="tw-py-4">
          @if (techSupportLiveChat) {
            <p>
              Live chat is available from 8:00 a.m. until 5:00 p.m. Central
              Time, Monday through Friday.
            </p>
            <div class="tw-pb-8" id="tsLiveChat">
              <!-- Genesys Live Chat button will be injected here when ready -->
            </div>
          }
          <p>Please feel free to call us at the following numbers and times:</p>
          <p class="tw-font-bold">
            Sedgwick Technical Support is available from 7:00 a.m. to 6:00 p.m.,
            Central Time, Monday through Friday.
          </p>
          <p>Toll-free Number: (866) 647-7610</p>
          <p>Toll Number: (901) 415-7800</p>

          @if (techSupportLiveChat && liveChatHasError) {
            <div class="tw-mt-4">
              <app-toast type="Info">
                <span>{{ liveChat.errorTitle }}</span>
                <br />
                <span>{{ liveChat.errorDetail }}</span>
              </app-toast>
            </div>
          }
        </div>
        <app-action-row>
          @if (sessionId) {
            <div left1>Session Id: {{ sessionId }}</div>
          }
          @if (requestId) {
            <div left1>Reference ID: {{ requestId }}</div>
          }

          <app-button
            right1
            (click)="supportDialog.visible = false"
            label="Close"
          />
        </app-action-row>
      </ng-template>
    </app-dialog>
  `,
  styles: `
    .logo {
      height: 38px;
    }
    .footer {
      color: var(--sed-dynamic-black-color);
    }
    a:hover,
    .footer-btn:hover {
      text-decoration-line: underline;
      text-decoration-style: solid;
      text-decoration-color: var(--sed-dynamic-black-color);
      text-underline-offset: var(--link-underline-offset);
      text-decoration-thickness: var(--link-underline-thickness-hover);
      outline: 2px solid var(--sed-dynamic-black-color);
      outline-offset: -2px;
    }
    .footer-btn {
      background: transparent;
      border: none;
      cursor: pointer;
      font-family: inherit;
      font-size: 18px;
      font-weight: 700;
      text-decoration-line: underline;
      text-decoration-style: solid;
      text-decoration-color: var(--sed-dynamic-black-color);
      text-underline-offset: var(--link-underline-offset);
      text-decoration-thickness: var(--link-underline-thickness);
    }

    .copyright {
      flex-wrap: nowrap !important;
      text-align: center !important;
    }

    /* Short landscape (e.g. S20 Ultra ~915×412): avoid footer links sitting under scroll-to-top */
    @media (orientation: landscape) and (max-height: 520px) {
      .footer-links-host {
        padding-inline-end: 4.5rem;
        padding-bottom: 0.25rem;
      }

      footer.footer {
        padding-bottom: max(0.5rem, env(safe-area-inset-bottom, 0px));
      }
    }
  `,
})
export class FooterComponent
  extends BaseComponent
  implements OnInit, AfterViewChecked
{
  protected liveChat = inject(LiveChatService);
  protected helpLink = inject(HelpLinkService);

  @ViewChild('supportDialog')
  supportDialog: DialogComponent;

  @Input()
  whatsNew = false;

  @Input()
  showFooterLogo = true;

  @Input()
  showFooterText = false;

  @Input()
  transparent = false;

  @Input()
  techSupportLiveChat = false;

  @Input()
  showWhatsNew = true;

  @ViewChild('aboutDialog')
  aboutDialog: DialogComponent;

  lastSupportDialogVisible = false;
  liveChatHasError = false;
  buildVersion: BuildVersion;

  privacyUrl = '';
  termsUrl = '';
  helpUrl = '';

  footer: Action[] = [];

  sessionId = '';
  requestId = '';

  private messageBus = inject(MessageBus);

  async ngOnInit() {
    this.footer.push({ label: 'About', key: 'about', routerLink: '' });
    if (this.showWhatsNew) {
      this.footer.push({
        label: 'What&apos;s new',
        key: 'whatsnew',
        routerLink: '',
      });
    }

    this.footer.push({
      label: 'Privacy notice',
      key: 'privacy',
      routerLink: '',
    });
    this.footer.push({
      label: 'Terms &amp; conditions',
      key: 'terms',
      routerLink: '',
    });
    this.footer.push({
      label: 'Help',
      key: 'help',
      routerLink: '',
    });
    this.footer.push({
      label: 'Technical support',
      key: 'support',
      routerLink: '',
    });

    this.dispose.subs = this.messageBus
      .topic('show-technical-support')
      .subscribe(async (value: string) => {
        this.requestId = value || '';
        await this.onSupportClick();
      });

    await this.setFooter();
    const config = await this.api.configs.get();
    this.buildVersion = config.buildVersion;
  }

  //Check if the live chat is available only after opening the support dialog, ensure
  // the dialog is not already open
  async ngAfterViewChecked(): Promise<void> {
    const isDialogVisible = !!(
      this.supportDialog && this.supportDialog.visible
    );

    const dialogJustOpened =
      isDialogVisible && !this.lastSupportDialogVisible;

    const dialogJustClosed =
      !isDialogVisible && this.lastSupportDialogVisible;

    if (dialogJustOpened) {
      this.runLiveChatIfAvailable();
    }

    if (dialogJustClosed) {
      // Just close the messenger popup if open, don't reset Genesys
      // Genesys cannot be reliably re-initialized after cleanup
      this.liveChat.closeChat();
      this.liveChatHasError = false;
    }

    this.lastSupportDialogVisible = isDialogVisible;
  }

  // Check if the chat is available, run if so, else set false to show toast
  private async runLiveChatIfAvailable(): Promise<void> {
    // Reset error state before checking
    this.liveChatHasError = false;

    await this.liveChat.doLiveChat();

    // Check status once after initialization completes
    this.liveChatHasError = this.liveChat.chatStatus() === 'error';
  }

  async setFooter(): Promise<void> {
    const config = await this.api.configs.get();
    const footerValues = config.footer;
    this.privacyUrl = footerValues.privacyPolicyUrl;
    this.termsUrl = footerValues.termsOfServiceUrl;
    this.helpUrl = footerValues.helpUrl;

    const whatsNewItem = this.footer.find((item) => item.key === 'whatsnew');
    if (whatsNewItem) {
      whatsNewItem.routerLink = footerValues.whatsNewUrl;
    }

    const privacyItem = this.footer.find((item) => item.key === 'privacy');
    if (privacyItem) {
      privacyItem.routerLink = this.privacyUrl;
    }

    const termsItem = this.footer.find((item) => item.key === 'terms');
    if (termsItem) {
      termsItem.routerLink = this.termsUrl;
    }

    // Help URL is resolved on click via HelpLinkService; no static routerLink
  }

  onAboutClick(event: Event) {
    event.preventDefault();
    this.aboutDialog.visible = true;
  }

  async onSupportClick() {
    this.sessionId = this.session.user?.sessionId ?? '';
    if (!this.sessionId) {
      try {
        this.sessionId = await this.api.session.id();
      } catch {
        // Unauthenticated users won't have a session id.
      }
    }
    this.supportDialog.visible = true;
  }

  async onHelpClick(): Promise<void> {
    const url = await this.helpLink.getHelpUrl();
    if (url) {
      window.open(url, '_blank');
    }
  }
}
